import React from 'react';
import { Pill } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@roger/ui-components';
import type { PatientMedicationResponse } from '../../services/api';
import AddMedicationButton, {
  type AddMedicationMode,
} from './AddMedicationButton';
import MedicationsEmptyState from './MedicationsEmptyState';
import MedicationsErrorState from './MedicationsErrorState';
import MedicationsLoadingState from './MedicationsLoadingState';
import MedicationsTable from './MedicationsTable';

interface MedicationsCardProps {
  medications: PatientMedicationResponse[];
  isLoading: boolean;
  error: string | null;
  onRetry: () => void;
  onAddMedication: (mode: AddMedicationMode) => void;
  onEditMedication: (medication: PatientMedicationResponse) => void;
  onDeleteMedication: (medication: PatientMedicationResponse) => void;
  onDiscontinueMedication: (medicationId: string, date: Date) => Promise<void>;
}

const MedicationsCard: React.FC<MedicationsCardProps> = ({
  medications,
  isLoading,
  error,
  onRetry,
  onAddMedication,
  onEditMedication,
  onDeleteMedication,
  onDiscontinueMedication,
}) => {
  return (
    <Card>
      <CardHeader>
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-2'>
            <Pill className='h-5 w-5' />
            <div>
              <CardTitle>Medications</CardTitle>
              <CardDescription>
                Search and manage patient medications
              </CardDescription>
            </div>
          </div>
          <AddMedicationButton onSelect={onAddMedication} />
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <MedicationsLoadingState />
        ) : error ? (
          <MedicationsErrorState error={error} onRetry={onRetry} />
        ) : medications.length === 0 ? (
          <MedicationsEmptyState />
        ) : (
          <MedicationsTable
            medications={medications}
            onEdit={onEditMedication}
            onDelete={onDeleteMedication}
            onDiscontinue={onDiscontinueMedication}
          />
        )}
      </CardContent>
    </Card>
  );
};

export default MedicationsCard;
